/**
 * 📅 MonKilometrageSemaine — v13.63 Phase 5 (2026-02-28)
 *
 * Récap 7 derniers jours de kilométrage pour l'employé courant. Affichée
 * dans Mon Espace sous `MonKilometrage` (aujourd'hui). Utilise la même
 * clé RBAC `mon_espace_kilometrage` — aucune nouvelle permission.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Car, Loader2, Calendar as CalendarIcon, TrendingUp } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

function fmt2(v) { return (v ?? 0).toFixed(2); }
function fmtMoney(v) { return `${fmt2(v)} $`; }

export function MonKilometrageSemaine({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/mon-espace/kilometrage/ma-semaine`,
        { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <Card data-testid="km-semaine-loading">
        <CardContent className="p-3 flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement du kilométrage de la semaine…
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.jours || data.jours.length === 0) return null;

  const nb = data.nb_jours_avec_km || 0;
  if (nb === 0) return null;

  const totalKm = data.total_payable_km || 0;
  const totalMnt = data.total_montant_dollars || 0;

  return (
    <Card
      className="border border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50"
      data-testid="km-semaine-card"
    >
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center shrink-0">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                Kilométrage — 7 derniers jours
              </p>
              <p className="text-xs text-teal-800">
                {nb} journée{nb > 1 ? 's' : ''} avec kilométrage
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-500 uppercase">Total semaine</p>
            <p className="text-base font-bold text-teal-700" data-testid="km-semaine-total">
              {fmtMoney(totalMnt)}
            </p>
            <p className="text-[10px] text-slate-500">{fmt2(totalKm)} km payables</p>
          </div>
        </div>

        <div className="space-y-1">
          {data.jours.filter(j => j.km_payables > 0).map((j) => {
            const isAdj = j.source === 'ajustement';
            const isRefused = isAdj && (j.ajustement?.statut === 'refuse');
            const dateLabel = new Date(`${j.date_iso}T12:00:00`).toLocaleDateString('fr-CA', {
              weekday: 'short', day: 'numeric', month: 'short',
            });
            return (
              <div key={j.date_iso}
                   className="flex items-center justify-between text-[11px] bg-white/70 px-2 py-1 rounded"
                   data-testid={`km-semaine-row-${j.date_iso}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <CalendarIcon className="w-3 h-3 text-slate-400 shrink-0" />
                  <span className="text-slate-700 font-medium">{dateLabel}</span>
                  {isAdj && (
                    <Badge className={`text-[9px] ${isRefused ? 'bg-red-100 text-red-700 border-red-300' : 'bg-amber-100 text-amber-800 border-amber-300'}`}
                           title="Valeur ajustée par l'administration">
                      Ajusté par l'admin
                    </Badge>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <span className={`font-semibold ${isRefused ? 'text-red-600' : 'text-teal-700'}`}>
                    {fmt2(j.km_payables)} km · {fmtMoney(j.montant_dollars)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-[10px] text-slate-400 text-center pt-1 flex items-center justify-center gap-1">
          <Car className="w-2.5 h-2.5" />
          Les montants sont figés au jour du déplacement — un changement de taux futur n'affecte pas ces jours.
        </p>
      </CardContent>
    </Card>
  );
}
